import { useState, useCallback } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import LinearProgress from '@mui/material/LinearProgress';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';

import {
  GET_SUBSCRIPTIONS,
  GET_SUBSCRIPTION_STATS,
  GET_PLANS,
  CREATE_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  EXTEND_SUBSCRIPTION,
} from './subscription.queries';

// Query pour rechercher les utilisateurs
const SEARCH_USERS = gql`
  query SearchUsers($search: String, $limit: Int) {
    publicProfiles(search: $search, limit: $limit) {
      id
      userId
      email
      displayName
      firstName
      lastName
      avatarUrl
    }
  }
`;
import {
  AdminUserSubscription,
  AdminSubscriptionStatus,
  AdminBillingPeriod,
  AdminPlan,
  SubscriptionStats,
  SubscriptionConnection,
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_COLORS,
  BILLING_PERIOD_LABELS,
  formatPrice,
  formatDate,
} from './subscription.types';

// Interface pour un utilisateur recherché
interface SearchedUser {
  id: string;
  userId: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
}

// ============================================================================
// STATS CARDS
// ============================================================================

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

function StatsCard({ title, value, subtitle, icon, color, loading }: StatsCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={80} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={700}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// ACTION DIALOGS
// ============================================================================

interface CancelDialogProps {
  open: boolean;
  subscription: AdminUserSubscription | null;
  onClose: () => void;
  onConfirm: (reason: string, immediate: boolean) => void;
  loading: boolean;
}

function CancelDialog({ open, subscription, onClose, onConfirm, loading }: CancelDialogProps) {
  const [reason, setReason] = useState('');
  const [immediate, setImmediate] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Annuler l'abonnement</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Êtes-vous sûr de vouloir annuler l'abonnement de{' '}
          <strong>{subscription?.user?.email || subscription?.enterprise?.name || 'cet utilisateur'}</strong> ?
        </Typography>
        <TextField
          label="Raison de l'annulation"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth>
          <InputLabel>Type d'annulation</InputLabel>
          <Select
            value={immediate ? 'immediate' : 'end_of_period'}
            label="Type d'annulation"
            onChange={(e) => setImmediate(e.target.value === 'immediate')}
          >
            <MenuItem value="end_of_period">À la fin de la période</MenuItem>
            <MenuItem value="immediate">Immédiatement</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={() => onConfirm(reason, immediate)} color="error" variant="contained" disabled={loading}>
          {loading ? 'Annulation...' : 'Confirmer l\'annulation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ExtendDialogProps {
  open: boolean;
  subscription: AdminUserSubscription | null;
  onClose: () => void;
  onConfirm: (days: number, reason: string) => void;
  loading: boolean;
}

function ExtendDialog({ open, subscription, onClose, onConfirm, loading }: ExtendDialogProps) {
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Prolonger l'abonnement</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Prolonger l'abonnement de{' '}
          <strong>{subscription?.user?.email || subscription?.enterprise?.name || 'cet utilisateur'}</strong>
        </Typography>
        <TextField
          label="Nombre de jours"
          type="number"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Raison"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={() => onConfirm(days, reason)} variant="contained" disabled={loading || days <= 0}>
          {loading ? 'Prolongation...' : `Prolonger de ${days} jours`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// CREATE SUBSCRIPTION DIALOG
// ============================================================================

interface CreateSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  plans: AdminPlan[];
  plansLoading: boolean;
}

function CreateSubscriptionDialog({
  open,
  onClose,
  onSuccess,
  plans,
  plansLoading
}: CreateSubscriptionDialogProps) {
  const notify = useNotify();

  // Form state
  const [selectedUser, setSelectedUser] = useState<SearchedUser | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [billingPeriod, setBillingPeriod] = useState<AdminBillingPeriod>(AdminBillingPeriod.MONTHLY);
  const [skipTrial, setSkipTrial] = useState(false);
  const [discountPercent, setDiscountPercent] = useState<number | ''>('');
  const [discountCode, setDiscountCode] = useState('');

  // User search state
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userOptions, setUserOptions] = useState<SearchedUser[]>([]);

  // Search users query
  const [searchUsers, { loading: searchingUsers }] = useLazyQuery(SEARCH_USERS, {
    onCompleted: (data) => {
      setUserOptions(data?.publicProfiles || []);
    },
    fetchPolicy: 'network-only',
  });

  // Create subscription mutation
  const [createSubscription, { loading: creating }] = useMutation(CREATE_SUBSCRIPTION);

  // Debounced search
  const handleUserSearch = useCallback((searchValue: string) => {
    if (searchValue.length >= 2) {
      searchUsers({ variables: { search: searchValue, limit: 10 } });
    }
  }, [searchUsers]);

  // Get selected plan details
  const selectedPlanDetails = plans.find(p => p.code === selectedPlan);
  const price = billingPeriod === AdminBillingPeriod.YEARLY
    ? (selectedPlanDetails?.yearlyPrice ?? selectedPlanDetails?.monthlyPrice ?? 0)
    : (selectedPlanDetails?.monthlyPrice ?? 0);
  const finalPrice = discountPercent
    ? price * (1 - Number(discountPercent) / 100)
    : price;

  // Reset form when dialog closes
  const handleClose = () => {
    setSelectedUser(null);
    setSelectedPlan('');
    setBillingPeriod(AdminBillingPeriod.MONTHLY);
    setSkipTrial(false);
    setDiscountPercent('');
    setDiscountCode('');
    setUserSearchInput('');
    setUserOptions([]);
    onClose();
  };

  // Handle create
  const handleCreate = async () => {
    if (!selectedUser?.userId || !selectedPlan) {
      notify('Veuillez sélectionner un utilisateur et un forfait', { type: 'warning' });
      return;
    }

    try {
      await createSubscription({
        variables: {
          planCode: selectedPlan,
          userId: selectedUser.userId,
          billingPeriod,
          skipTrial,
          discountPercent: discountPercent ? Number(discountPercent) : undefined,
          discountCode: discountCode || undefined,
        },
      });
      notify('Abonnement créé avec succès', { type: 'success' });
      handleClose();
      onSuccess();
    } catch (error: any) {
      notify(`Erreur: ${error.message}`, { type: 'error' });
    }
  };

  // Filter active plans
  const activePlans = plans.filter(p => p.status === 'ACTIVE');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Créer un abonnement</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {/* User Search */}
          <Autocomplete
            options={userOptions}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            inputValue={userSearchInput}
            onInputChange={(_, newInputValue) => {
              setUserSearchInput(newInputValue);
              handleUserSearch(newInputValue);
            }}
            getOptionLabel={(option) => {
              const name = option.displayName || `${option.firstName || ''} ${option.lastName || ''}`.trim();
              return option.email ? `${name} (${option.email})` : name || option.id;
            }}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            loading={searchingUsers}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={option.avatarUrl} sx={{ width: 32, height: 32 }}>
                    {(option.displayName || option.email || '?')[0]?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {option.displayName || `${option.firstName || ''} ${option.lastName || ''}`.trim() || 'Sans nom'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Utilisateur"
                placeholder="Rechercher par nom ou email..."
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchingUsers && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Plan Selection */}
          <FormControl fullWidth required>
            <InputLabel>Forfait</InputLabel>
            <Select
              value={selectedPlan}
              label="Forfait"
              onChange={(e) => setSelectedPlan(e.target.value)}
              disabled={plansLoading}
            >
              {plansLoading && <MenuItem value="">Chargement...</MenuItem>}
              {activePlans.map((plan) => (
                <MenuItem key={plan.code} value={plan.code}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: plan.colorPrimary || '#6B7280'
                        }}
                      />
                      <span>{plan.name}</span>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatPrice(plan.monthlyPrice)}/mois
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Billing Period */}
          <FormControl fullWidth>
            <InputLabel>Période de facturation</InputLabel>
            <Select
              value={billingPeriod}
              label="Période de facturation"
              onChange={(e) => setBillingPeriod(e.target.value as AdminBillingPeriod)}
            >
              <MenuItem value={AdminBillingPeriod.MONTHLY}>
                Mensuel
              </MenuItem>
              <MenuItem value={AdminBillingPeriod.YEARLY}>
                Annuel {selectedPlanDetails?.yearlyDiscount ? `(-${selectedPlanDetails.yearlyDiscount}%)` : ''}
              </MenuItem>
            </Select>
          </FormControl>

          {/* Skip Trial */}
          {selectedPlanDetails && selectedPlanDetails.trialDays > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={skipTrial}
                  onChange={(e) => setSkipTrial(e.target.checked)}
                />
              }
              label={`Ignorer la période d'essai (${selectedPlanDetails.trialDays} jours)`}
            />
          )}

          {/* Discount */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Réduction (%)"
              type="number"
              value={discountPercent}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || (Number(val) >= 0 && Number(val) <= 100)) {
                  setDiscountPercent(val === '' ? '' : Number(val));
                }
              }}
              sx={{ flex: 1 }}
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              label="Code promo"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              sx={{ flex: 1 }}
              placeholder="Ex: PROMO2026"
            />
          </Box>

          {/* Price Summary */}
          {selectedPlanDetails && (
            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Récapitulatif
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Forfait:
                </Typography>
                <Typography variant="body2">
                  {selectedPlanDetails.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Période:
                </Typography>
                <Typography variant="body2">
                  {BILLING_PERIOD_LABELS[billingPeriod]}
                </Typography>
              </Box>
              {discountPercent && Number(discountPercent) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Réduction:
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    -{discountPercent}%
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" fontWeight={600}>
                  Prix final:
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary">
                  {formatPrice(finalPrice)} / {billingPeriod === AdminBillingPeriod.YEARLY ? 'an' : 'mois'}
                </Typography>
              </Box>
              {!skipTrial && selectedPlanDetails.trialDays > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  * L'utilisateur bénéficiera de {selectedPlanDetails.trialDays} jours d'essai gratuit
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Annuler</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating || !selectedUser || !selectedPlan}
          startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {creating ? 'Création...' : 'Créer l\'abonnement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SubscriptionList() {
  const notify = useNotify();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<AdminUserSubscription | null>(null);

  // Status filter based on tab
  const statusFilter = tabValue === 0
    ? undefined
    : tabValue === 1
    ? 'ACTIVE'
    : tabValue === 2
    ? 'TRIAL'
    : tabValue === 3
    ? 'CANCELLED'
    : 'EXPIRED';

  // Queries
  const { data: statsData, loading: statsLoading, refetch: refetchStats } = useQuery(GET_SUBSCRIPTION_STATS, {
    fetchPolicy: 'cache-and-network', // Toujours récupérer depuis le serveur
  });
  const { data, loading, error, refetch } = useQuery(GET_SUBSCRIPTIONS, {
    variables: {
      status: statusFilter,
      page: page + 1,
      pageSize: rowsPerPage,
    },
  });
  const { data: plansData, loading: plansLoading } = useQuery(GET_PLANS, { variables: { activeOnly: false } });
  const plans: AdminPlan[] = plansData?.adminPlans || [];

  // Rafraîchir liste et stats ensemble
  const refreshAll = () => {
    refetch();
    refetchStats();
  };

  // Mutations
  const [cancelSubscription, { loading: cancelling }] = useMutation(CANCEL_SUBSCRIPTION);
  const [reactivateSubscription, { loading: reactivating }] = useMutation(REACTIVATE_SUBSCRIPTION);
  const [extendSubscription, { loading: extending }] = useMutation(EXTEND_SUBSCRIPTION);

  const stats: SubscriptionStats | null = statsData?.adminSubscriptionStats;
  const subscriptions: SubscriptionConnection = data?.adminSubscriptions || { items: [], total: 0, page: 1, pageSize: 20 };

  // Handlers
  const handleCancel = async (reason: string, immediate: boolean) => {
    if (!selectedSubscription) return;
    try {
      await cancelSubscription({
        variables: { id: selectedSubscription.id, reason, immediate },
      });
      notify('Abonnement annulé', { type: 'success' });
      setCancelDialogOpen(false);
      refreshAll();
    } catch {
      notify('Erreur lors de l\'annulation', { type: 'error' });
    }
  };

  const handleReactivate = async (subscription: AdminUserSubscription) => {
    try {
      await reactivateSubscription({ variables: { id: subscription.id } });
      notify('Abonnement réactivé', { type: 'success' });
      refreshAll();
    } catch {
      notify('Erreur lors de la réactivation', { type: 'error' });
    }
  };

  const handleExtend = async (days: number, reason: string) => {
    if (!selectedSubscription) return;
    try {
      await extendSubscription({
        variables: { id: selectedSubscription.id, days, reason },
      });
      notify(`Abonnement prolongé de ${days} jours`, { type: 'success' });
      setExtendDialogOpen(false);
      refreshAll();
    } catch {
      notify('Erreur lors de la prolongation', { type: 'error' });
    }
  };

  const getStatusColor = (status: AdminSubscriptionStatus) => SUBSCRIPTION_STATUS_COLORS[status] || '#6B7280';

  const isExpiringSoon = (subscription: AdminUserSubscription) => {
    const daysUntilEnd = Math.ceil((new Date(subscription.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return daysUntilEnd <= 7 && daysUntilEnd > 0;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Abonnements" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Gestion des abonnements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suivez et gérez les abonnements de vos utilisateurs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Créer un abonnement
          </Button>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={refreshAll}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Abonnements actifs"
            value={stats?.byStatus?.ACTIVE || 0}
            icon={<PeopleIcon />}
            color="#10B981"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="En période d'essai"
            value={stats?.byStatus?.TRIAL || 0}
            icon={<EventIcon />}
            color="#8B5CF6"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="MRR"
            value={stats ? formatPrice(stats.mrr) : '-'}
            subtitle="Revenu mensuel récurrent"
            icon={<AttachMoneyIcon />}
            color="#F59E0B"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total abonnements"
            value={stats?.total || 0}
            subtitle={stats ? `ARR: ${formatPrice(stats.arr)}` : undefined}
            icon={<TrendingDownIcon />}
            color="#6366F1"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => { setTabValue(v); setPage(0); }}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Tous (${subscriptions.total})`} />
        <Tab label="Actifs" />
        <Tab label="Essai" />
        <Tab label="Annulés" />
        <Tab label="Expirés" />
      </Tabs>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur: {error.message}
        </Alert>
      )}

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Forfait</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Période</TableCell>
              <TableCell>Prix</TableCell>
              <TableCell>Date de fin</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    Aucun abonnement trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {subscriptions.items.map((sub) => (
              <TableRow
                key={sub.id}
                sx={{
                  backgroundColor: isExpiringSoon(sub) ? 'warning.light' : undefined,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                      {sub.user?.email?.[0]?.toUpperCase() || sub.enterprise?.name?.[0] || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {sub.user?.email || sub.enterprise?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.enterprise ? 'Enterprise' : 'Utilisateur'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={sub.plan?.name || 'N/A'}
                    size="small"
                    sx={{
                      backgroundColor: sub.plan?.colorPrimary ? `${sub.plan.colorPrimary}20` : undefined,
                      color: sub.plan?.colorPrimary,
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={SUBSCRIPTION_STATUS_LABELS[sub.status]}
                    size="small"
                    sx={{
                      backgroundColor: `${getStatusColor(sub.status)}20`,
                      color: getStatusColor(sub.status),
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {BILLING_PERIOD_LABELS[sub.billingPeriod]}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {formatPrice(sub.priceAmount, sub.priceCurrency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isExpiringSoon(sub) && (
                      <Tooltip title="Expire bientôt">
                        <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                      </Tooltip>
                    )}
                    <Typography variant="body2">
                      {formatDate(sub.endDate)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Voir détails">
                      <IconButton size="small" onClick={() => navigate(`/subscriptions/${sub.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(sub.status === AdminSubscriptionStatus.ACTIVE || sub.status === AdminSubscriptionStatus.TRIAL) && (
                      <>
                        <Tooltip title="Prolonger">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedSubscription(sub);
                              setExtendDialogOpen(true);
                            }}
                          >
                            <MoreTimeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Annuler">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedSubscription(sub);
                              setCancelDialogOpen(true);
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {sub.status === AdminSubscriptionStatus.CANCELLED && (
                      <Tooltip title="Réactiver">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleReactivate(sub)}
                          disabled={reactivating}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={subscriptions.total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Lignes par page"
        />
      </TableContainer>

      {/* Dialogs */}
      <CancelDialog
        open={cancelDialogOpen}
        subscription={selectedSubscription}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancel}
        loading={cancelling}
      />
      <ExtendDialog
        open={extendDialogOpen}
        subscription={selectedSubscription}
        onClose={() => setExtendDialogOpen(false)}
        onConfirm={handleExtend}
        loading={extending}
      />
      <CreateSubscriptionDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={refreshAll}
        plans={plans}
        plansLoading={plansLoading}
      />
    </Box>
  );
}

export default SubscriptionList;
